import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { UserPlus, Users, Check, X, Loader2, Inbox } from 'lucide-react'
import { teamsAPI } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import PageHeader from '@/components/PageHeader'

/* Mijn team-verzoeken — persoonlijke inbox voor:
   - uitnodigingen naar mij (ik accepteer of weiger)
   - join-requests op teams waar IK kapitein van ben (ik keur goed of weiger)
   Bewust GEEN admin-endpoint. Team-verzoeken horen bij de betrokken user
   of team-lead, niet bij de organisatie. */

function fmtDur(ts) {
  if (!ts) return ''
  const d = (Date.now() - new Date(ts).getTime()) / 60000
  if (d < 1) return 'zojuist'
  if (d < 60) return `${Math.floor(d)}m`
  if (d < 1440) return `${Math.floor(d/60)}u`
  return `${Math.floor(d/1440)}d`
}

export default function TeamRequestsInboxPage() {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(null)
  const [msg, setMsg] = useState('')

  const load = async () => {
    try {
      const r = await teamsAPI.myPending()
      setData(r.data)
    } catch (e) {
      setMsg(`Fout: ${e.response?.data?.error ?? e.message}`)
    }
  }
  useEffect(() => { load() }, [])

  const respond = async (inviteId, accept) => {
    setBusy(inviteId)
    try {
      await teamsAPI.respondInvite(inviteId, accept)
      await load()
    } catch (e) {
      setMsg(`Fout: ${e.response?.data?.error ?? e.message}`)
    } finally { setBusy(null) }
  }

  if (data === null) return (
    <div className="p-6"><Loader2 className="animate-spin" /></div>
  )

  const invites  = data.invites || []
  const requests = data.requests || []

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <PageHeader
        icon={Inbox}
        title="Mijn team-verzoeken"
        subtitle="Uitnodigingen aan jou en aanvragen voor teams waar jij kapitein van bent"
      />

      {msg && <div className="text-sm text-signal">{msg}</div>}

      {invites.length === 0 && requests.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Geen openstaande verzoeken.
          </CardContent>
        </Card>
      )}

      {invites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus size={16} /> Uitnodigingen voor jou ({invites.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {invites.map(r => (
              <div key={r.id} className="flex items-center gap-3 p-3 rounded-md border border-border">
                <div className="flex-1 min-w-0 text-sm">
                  <div className="truncate">
                    Uitnodiging voor{' '}
                    <Link to={`/teams/${r.team_id}`}
                          className="hover:text-acid underline decoration-dotted font-semibold">
                      {r.team_name}
                    </Link>
                    {r.created_by_username && (
                      <span className="text-muted-foreground">
                        {' — van '}
                        {r.created_by_id ? (
                          <Link to={`/spelers/${r.created_by_id}`}
                                className="hover:text-acid underline decoration-dotted">
                            @{r.created_by_username}
                          </Link>
                        ) : `@${r.created_by_username}`}
                      </span>
                    )}
                  </div>
                  {r.message && <div className="text-xs text-muted-foreground italic mt-0.5">"{r.message}"</div>}
                </div>
                <span className="font-mono text-[10px] text-muted-foreground shrink-0">{fmtDur(r.created_at)}</span>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" disabled={busy === r.id} onClick={() => respond(r.id, true)}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Aannemen
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => respond(r.id, false)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {requests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users size={16} /> Aanvragen voor jouw teams ({requests.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {requests.map(r => (
              <div key={r.id} className="flex items-center gap-3 p-3 rounded-md border border-border">
                <Link to={`/spelers/${r.user_id}`} target="_blank" rel="noopener noreferrer" className="shrink-0"
                      title="Open profiel in nieuw tabblad">
                  {r.avatar_url
                    ? <img src={r.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover hover:ring-2 hover:ring-acid transition-shadow" />
                    : <div className="h-8 w-8 rounded-full bg-muted grid place-items-center text-xs hover:ring-2 hover:ring-acid transition-shadow">
                        {(r.display_name || r.username || '?').slice(0, 1).toUpperCase()}
                      </div>}
                </Link>
                <div className="flex-1 min-w-0 text-sm">
                  <div className="truncate">
                    <Link to={`/spelers/${r.user_id}`} target="_blank" rel="noopener noreferrer"
                          className="font-semibold hover:text-acid underline decoration-dotted"
                          title="Open profiel in nieuw tabblad">
                      {r.display_name || r.username}
                    </Link>{' '}
                    <span className="text-muted-foreground">wil bij</span>{' '}
                    <Link to={`/teams/${r.team_id}`}
                          className="hover:text-acid underline decoration-dotted">
                      {r.team_name}
                    </Link>
                  </div>
                  {r.message && <div className="text-xs text-muted-foreground italic mt-0.5">"{r.message}"</div>}
                </div>
                <span className="font-mono text-[10px] text-muted-foreground shrink-0">{fmtDur(r.created_at)}</span>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" disabled={busy === r.id} onClick={() => respond(r.id, true)}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Goedkeuren
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => respond(r.id, false)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
